import { useCallback, useEffect, useMemo } from 'react';
import type {
  UseOnClickOutsideSettings,
  TouchOrMouseEventCallback,
} from './useOnClickOutside.typed';

const stopPropagation = true;
const registry: { [id: number]: TouchOrMouseEventCallback } = {};

const handleClick = (event: MouseEvent) => {
  if (!event.defaultPrevented) {
    for (const handler of Object.values(registry).reverse()) {
      if (handler(event) === stopPropagation || event.defaultPrevented) {
        break;
      }
    }
  }
};

const register = (id: number, handler: TouchOrMouseEventCallback): void => {
  registry[id] = handler;
};
const deregister = (id: number) => {
  delete registry[id];
};

let handlerId = 0;

export const useOnClickOutside = (props: UseOnClickOutsideSettings) => {
  const id = useMemo(() => handlerId++, []);
  const { onClickOutside, ignoreClickRefs, container } = props;

  const handler = useCallback<TouchOrMouseEventCallback>(
    (event) => {
      event.stopPropagation();
      if (container && container.contains(event.target as Node)) {
        return stopPropagation;
      }
      if (ignoreClickRefs) {
        for (const ref of ignoreClickRefs) {
          if (ref.current && ref.current.contains(event.target as Node)) {
            return stopPropagation;
          }
        }
      }
      onClickOutside(event);
      return false;
    },
    [container, ignoreClickRefs, onClickOutside],
  );

  useEffect(() => {
    if (Object.keys(registry).length === 0) {
      document.addEventListener('mousedown', handleClick, { capture: true });
    }
    register(id, handler);

    return () => {
      deregister(id);
      if (Object.keys(registry).length === 0) {
        document.removeEventListener('mousedown', handleClick, {
          capture: true,
        });
      }
    };
  }, [id, handler]);
};

export default useOnClickOutside;
